import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { makeT } from "../i18n";
import { api, setAuthToken, getAuthToken, getBioToken } from "../api";
import { initPushNotifications } from "../lib/push";
import { updateAppBadge } from "../lib/badge";
import { clearOfflineData } from "../lib/offlineSync";
import { startAppVersionWatch, stopAppVersionWatch, checkAppVersion } from "../lib/appVersion";
import { startLiveUpdateWatch } from "../lib/liveUpdate";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [user, setUserState] = useState(() => {
    try {
      if (!getAuthToken()) return null; // no token = not authenticated
      const raw = localStorage.getItem("pmf_user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const [lang, setLangState] = useState(() => localStorage.getItem("pmf_lang") || "en");
  const [authChecked, setAuthChecked] = useState(false);

  // Selecting/refreshing a user loads THAT user's saved language preference
  const setUser = useCallback((u) => {
    setUserState(u);
    if (u) {
      localStorage.setItem("pmf_user", JSON.stringify(u));
      if (["en", "pt", "es"].includes(u.language)) {
        setLangState(u.language);
        localStorage.setItem("pmf_lang", u.language);
      } else {
        // First login: save the current app language as their preference
        const current = localStorage.getItem("pmf_lang") || "en";
        api.patch(`/users/${u.id}`, { language: current }).catch(() => {});
        const updated = { ...u, language: current };
        setUserState(updated);
        localStorage.setItem("pmf_user", JSON.stringify(updated));
      }
    } else {
      localStorage.removeItem("pmf_user");
    }
  }, []);

  // Authenticated login: store JWT + user
  const login = useCallback((token, u) => {
    setAuthToken(token);
    setUser(u);
    // Remember the email (not the password) to prefill the next sign-in
    try { if (u?.email) localStorage.setItem("pmf_last_email", u.email); } catch (e) {}
  }, [setUser]);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    updateAppBadge(0); // clear the app-icon unread badge on sign out
    clearOfflineData().catch(() => {}); // the offline mirror never outlives the session on this device
    // Explicit sign-out: skip the automatic FaceID prompt once on the login screen
    try { sessionStorage.setItem("pmf_skip_auto_passkey", "1"); } catch (e) {}
  }, [setUser]);

  // Native apps: register this device for push notifications (no-op on web)
  useEffect(() => {
    if (user?.id) initPushNotifications(user.id);
  }, [user?.id]);

  // Native apps: report installed version + show the yellow update bar when outdated
  useEffect(() => {
    if (user?.id) startAppVersionWatch();
    else { stopAppVersionWatch(); checkAppVersion(); } // logged out: still enforce the mandatory update
  }, [user?.id]);

  // Native apps: pull the latest deployed web bundle (OTA) — runs logged in or out
  useEffect(() => { startLiveUpdateWatch(); }, []);

  // Validate session on load (30-day JWT)
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setUserState(null);
      // Face ID lock ON keeps a biometric-gated token: keep the cached profile too, so the app can open
      // OFFLINE right after Face ID (validated against the server as soon as we are online again)
      if (!getBioToken()) localStorage.removeItem("pmf_user");
      setAuthChecked(true);
      return;
    }
    api.get("/auth/me", { timeout: 10000 })
      .then((res) => {
        const u = res.data;
        setUserState(u);
        localStorage.setItem("pmf_user", JSON.stringify(u));
        if (["en", "pt", "es"].includes(u.language)) {
          setLangState(u.language);
          localStorage.setItem("pmf_lang", u.language);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          setAuthToken(null);
          localStorage.removeItem("pmf_user");
          setUserState(null);
        }
        // Network errors keep the cached user (offline-first behaviour)
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Changing language persists it to the active user's profile
  const setLang = useCallback((l) => {
    setLangState(l);
    localStorage.setItem("pmf_lang", l);
    setUserState((prev) => {
      if (prev) {
        api.patch(`/users/${prev.id}`, { language: l }).catch(() => {});
        const updated = { ...prev, language: l };
        localStorage.setItem("pmf_user", JSON.stringify(updated));
        return updated;
      }
      return prev;
    });
  }, []);

  const t = useMemo(() => makeT(lang), [lang]);

  const value = useMemo(
    () => ({ user, setUser, login, logout, authChecked, lang, setLang, t }),
    [user, setUser, login, logout, authChecked, lang, setLang, t]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
